import React, { useState, useEffect } from 'react';
import { 
  Wind, 
  Shield, 
  ArrowRight,
  Heart,
  MessageCircle,
  Star,
  Download,
  Menu,
  X,
  Instagram,
  Twitter,
  Linkedin
} from 'lucide-react';

// --- ESTILOS VISUALES (Mantenemos tu estilo favorito) ---
const CustomStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    :root {
      --primary: #47748A;
      --secondary: #91D7D5;
    }

    body {
      font-family: 'Plus Jakarta Sans', sans-serif;
      background-color: #FAFAFA;
      color: #000000;
      overflow-x: hidden;
    }

    /* Blob Backgrounds Animados */
    .blob-bg {
      position: absolute;
      background: radial-gradient(circle, rgba(145,215,213,0.3) 0%, rgba(255,255,255,0) 70%);
      border-radius: 50%;
      filter: blur(60px);
      z-index: -1;
      animation: float 20s infinite ease-in-out;
    }

    @keyframes float {
      0% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0, 0) scale(1); }
    }

    /* Glassmorphism para tarjetas */
    .glass-card {
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.9);
      box-shadow: 0 4px 20px rgba(0,0,0,0.02);
    }

    .glass-nav {
      background: rgba(250, 250, 250, 0.9);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(0,0,0,0.05);
    }

    /* Animaciones de Menú Móvil */
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .mobile-menu-enter {
      animation: slideDown 0.3s ease-out forwards;
    }
  `}</style>
);

// --- COMPONENTES UI ---

const FeatureCard = ({ icon: Icon, title, desc, delay }) => (
  <div 
    className="glass-card p-8 rounded-[32px] hover:translate-y-[-5px] transition-transform duration-300"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="w-14 h-14 bg-[#EAF6F6] rounded-2xl flex items-center justify-center text-[#47748A] mb-6">
      <Icon size={28} strokeWidth={2} />
    </div>
    <h3 className="text-xl font-bold mb-3 text-black">{title}</h3>
    <p className="text-gray-500 leading-relaxed text-sm md:text-base">{desc}</p>
  </div>
);

const StatNumber = ({ number, label }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-extrabold text-[#47748A] mb-2">{number}</div>
    <div className="text-sm font-bold uppercase tracking-wider text-gray-400">{label}</div>
  </div>
);

// --- LANDING PAGE COMPLETA ---

export default function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Bloquear scroll cuando el menú está abierto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden bg-[#FAFAFA]">
      <CustomStyles />
      
      {/* Blobs decorativos globales */}
      <div className="blob-bg w-[800px] h-[800px] top-[-20%] left-[-10%] opacity-40" />
      <div className="blob-bg w-[600px] h-[600px] bottom-[10%] right-[-10%] bg-blue-100 opacity-40" />

      {/* 1. NAVBAR MEJORADO */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isMenuOpen ? 'bg-white' : 'glass-nav'}`}>
        <div className="max-w-6xl mx-auto px-6 h-20 flex justify-between items-center relative z-50">
          
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo(0,0)}>
            <div className="w-8 h-8 bg-[#47748A] rounded-xl flex items-center justify-center text-white font-bold text-lg">T</div>
            <span className="text-xl font-bold tracking-tight text-black">trankii.</span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex gap-8 items-center text-sm font-medium text-gray-600">
            <a href="#como-funciona" className="hover:text-[#47748A] transition-colors">Cómo funciona</a>
            <a href="#beneficios" className="hover:text-[#47748A] transition-colors">Beneficios</a>
            <a href="#testimonios" className="hover:text-[#47748A] transition-colors">Historias</a>
            <button className="px-5 py-2 rounded-full bg-[#000000] text-white font-bold hover:bg-[#47748A] transition-colors transform hover:scale-105 duration-200">
              Descargar App
            </button>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button 
            className="md:hidden p-2 -mr-2 text-black hover:bg-gray-100 rounded-full transition-colors" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Overlay - Diseño Full Screen Moderno */}
        {isMenuOpen && (
          <div className="fixed inset-0 bg-white z-40 flex flex-col pt-24 px-6 mobile-menu-enter">
            
            {/* Background Decorations for Menu */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#EAF6F6] rounded-full blur-3xl opacity-50 -z-10 pointer-events-none"></div>

            <div className="flex flex-col gap-6 text-2xl font-bold text-black">
              <a 
                href="#como-funciona" 
                onClick={() => setIsMenuOpen(false)}
                className="py-4 border-b border-gray-100 flex justify-between items-center group"
              >
                Cómo funciona
                <ArrowRight size={20} className="text-gray-300 group-hover:text-[#47748A] -translate-x-4 group-hover:translate-x-0 transition-all opacity-0 group-hover:opacity-100" />
              </a>
              <a 
                href="#beneficios" 
                onClick={() => setIsMenuOpen(false)}
                className="py-4 border-b border-gray-100 flex justify-between items-center group"
              >
                Beneficios
                <ArrowRight size={20} className="text-gray-300 group-hover:text-[#47748A] -translate-x-4 group-hover:translate-x-0 transition-all opacity-0 group-hover:opacity-100" />
              </a>
              <a 
                href="#testimonios" 
                onClick={() => setIsMenuOpen(false)}
                className="py-4 border-b border-gray-100 flex justify-between items-center group"
              >
                Historias
                <ArrowRight size={20} className="text-gray-300 group-hover:text-[#47748A] -translate-x-4 group-hover:translate-x-0 transition-all opacity-0 group-hover:opacity-100" />
              </a>
            </div>

            <div className="mt-auto mb-10 space-y-4">
              <button className="w-full py-4 rounded-2xl bg-[#000000] text-white font-bold text-lg shadow-xl shadow-gray-200 active:scale-95 transition-transform flex justify-center items-center gap-2">
                <Download size={20} /> Descargar Gratis
              </button>
              <p className="text-center text-gray-400 text-sm">Versión 2.0 disponible para iOS & Android</p>
            </div>
          </div>
        )}
      </nav>

      {/* 2. HERO SECTION (Mantenemos el diseño que te gustó) */}
      <header className="pt-32 pb-20 px-6 text-center max-w-5xl mx-auto z-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-gray-100 mb-8 animate-fade-in-up">
          <span className="w-2 h-2 rounded-full bg-[#91D7D5] animate-pulse"></span>
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Disponible 24/7 para ti</span>
        </div>
        
        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter text-black mb-8 leading-[1.1] md:leading-[0.9]">
          Tu compañero emocional <br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#47748A] to-[#91D7D5]">
            sin juicios ni esperas.
          </span>
        </h1>
        
        <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
          Trankii convierte la terapia cognitivo-conductual en conversaciones simples. 
          Un espacio seguro para desahogarte, aprender y crecer a tu propio ritmo.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <button className="group px-8 py-4 bg-[#000000] text-white rounded-full font-bold text-lg shadow-xl shadow-gray-900/20 hover:scale-105 transition-transform flex items-center gap-3 w-full sm:w-auto justify-center">
            <Download size={20} /> Descargar para iOS
          </button>
          <button className="px-8 py-4 bg-white text-black border border-gray-200 rounded-full font-bold text-lg hover:border-[#47748A] transition-colors flex items-center gap-3 w-full sm:w-auto justify-center">
             <Download size={20} /> Android
          </button>
        </div>

        {/* Abstract Phone Mockup (Visual Clave) */}
        <div className="relative w-full max-w-xs md:max-w-sm mx-auto h-[400px] md:h-[500px]">
          <div className="absolute inset-0 bg-gradient-to-b from-[#47748A] to-[#2c4b5a] rounded-[3rem] shadow-[0_25px_60px_-15px_rgba(71,116,138,0.5)] border-8 border-white overflow-hidden">
             {/* Screen Content Simulator */}
             <div className="w-full h-full bg-[#FAFAFA] relative flex flex-col">
                {/* Chat Header */}
                <div className="p-6 bg-white border-b border-gray-50 flex items-center gap-3">
                   <div className="w-10 h-10 rounded-full bg-[#91D7D5] flex items-center justify-center text-white font-bold">T</div>
                   <div>
                      <div className="text-sm font-bold">Trankii</div>
                      <div className="text-[10px] text-[#47748A] font-bold uppercase">En línea</div>
                   </div>
                </div>
                {/* Chat Bubbles */}
                <div className="p-6 space-y-4">
                   <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm max-w-[85%] text-sm text-gray-600">
                      Hola, Alex. Noté que hoy ha sido un día largo. ¿Cómo te sientes realmente?
                   </div>
                   <div className="bg-[#47748A] text-white p-4 rounded-2xl rounded-tr-none shadow-md max-w-[85%] ml-auto text-sm">
                      Me siento un poco abrumado, la verdad. No sé por dónde empezar.
                   </div>
                   <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm max-w-[85%] text-sm text-gray-600 flex gap-2 items-center">
                      <Wind size={16} className="text-[#91D7D5]" />
                      <span>Entiendo. ¿Te gustaría hacer una pausa de 2 minutos para respirar juntos?</span>
                   </div>
                </div>
                {/* Floating Action */}
                <div className="absolute bottom-6 left-6 right-6">
                   <div className="bg-white p-4 rounded-2xl shadow-lg border border-gray-100 text-center text-[#47748A] font-bold text-sm cursor-pointer hover:bg-[#F5F9FA]">
                      Sí, empecemos
                   </div>
                </div>
             </div>
          </div>
        </div>
      </header>

      {/* 3. SOCIAL PROOF */}
      <section className="py-12 bg-white border-y border-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-sm font-bold text-gray-400 uppercase tracking-widest mb-8">
            Respaldado por la ciencia, amado por las personas
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-20 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
             {/* Logos ficticios de prensa/partners */}
             <div className="text-xl font-serif font-bold italic">The Wellness Times</div>
             <div className="text-xl font-bold tracking-tight">MIND<span className="font-light">CARE</span></div>
             <div className="text-xl font-bold flex gap-1 items-center"><div className="w-6 h-6 bg-black rounded-full"></div>SaludDigital</div>
             <div className="text-xl font-serif font-bold">Balance.</div>
          </div>
        </div>
      </section>

      {/* 4. CÓMO FUNCIONA (BENTO GRID) */}
      <section id="como-funciona" className="py-24 max-w-6xl mx-auto px-6">
        <div className="mb-16 md:text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-black">No es magia, es <span className="text-[#47748A]">inteligencia emocional.</span></h2>
          <p className="text-gray-500">
            Trankii utiliza principios de Terapia Cognitivo-Conductual (TCC) para ayudarte a identificar y gestionar tus emociones en tiempo real.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <FeatureCard 
              icon={MessageCircle}
              title="Conversación Natural"
              desc="Habla con Trankii como si fuera un amigo. Sin formularios aburridos, solo una charla fluida que se adapta a tu estado de ánimo actual."
              delay={0}
            />
          </div>
          <FeatureCard 
            icon={Shield}
            title="100% Privado"
            desc="Tus emociones son tuyas. Todo es anónimo y encriptado. Ni siquiera nosotros podemos leer tus conversaciones."
            delay={100}
          />
          <FeatureCard 
            icon={Wind}
            title="Herramientas SOS"
            desc="Ejercicios de respiración y mindfulness de 3 minutos para momentos de crisis o ansiedad aguda."
            delay={200}
          />
          <div className="md:col-span-2">
             <div className="glass-card p-8 rounded-[32px] h-full flex flex-col md:flex-row items-center gap-8 hover:translate-y-[-5px] transition-transform bg-[#47748A] text-white">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-3 text-white">Seguimiento de Progreso</h3>
                  <p className="text-[#91D7D5] leading-relaxed mb-6">
                    Visualiza cómo mejora tu estado de ánimo con el tiempo. Detecta patrones y celebra tus días de calma.
                  </p>
                  <button className="text-sm font-bold bg-[#91D7D5] text-[#000000] px-6 py-2 rounded-full hover:bg-white transition-colors">
                    Ver demo
                  </button>
                </div>
                {/* Mini Graph UI */}
                <div className="w-full md:w-48 h-32 flex items-end gap-2 p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                   {[40, 60, 35, 70, 50, 80].map((h, i) => (
                      <div key={i} style={{height: `${h}%`}} className="flex-1 bg-[#91D7D5] rounded-t-lg opacity-80"></div>
                   ))}
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 5. TESTIMONIOS */}
      <section id="testimonios" className="py-24 bg-[#EAF6F6]/50 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
           <div className="mb-12">
             <div className="w-16 h-16 bg-[#91D7D5] rounded-full flex items-center justify-center text-black mx-auto mb-6">
               <Heart fill="black" size={24} />
             </div>
             <h2 className="text-3xl md:text-5xl font-bold text-black mb-8 leading-tight">
               "Por fin una app que no se siente como una tarea, sino como un <span className="underline decoration-[#91D7D5] decoration-4 underline-offset-4">abrazo</span>."
             </h2>
           </div>

           <div className="grid md:grid-cols-3 gap-8 text-left">
              {[
                { name: "Sofía M.", role: "Diseñadora", text: "Me ayuda a dormir mejor cuando mi cabeza no para de dar vueltas." },
                { name: "Carlos R.", role: "Estudiante", text: "La uso antes de mis exámenes. Los ejercicios de respiración son oro puro." },
                { name: "Ana P.", role: "Mamá primeriza", text: "Sentirme escuchada a las 3 AM sin despertar a nadie no tiene precio." }
              ].map((t, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                   <div className="flex text-[#47748A] mb-3">
                     {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                   </div>
                   <p className="text-gray-600 text-sm mb-4">"{t.text}"</p>
                   <div className="font-bold text-sm text-black">{t.name}</div>
                   <div className="text-xs text-gray-400">{t.role}</div>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 6. STATS & FINAL CTA */}
      <section className="py-24 px-6 bg-white">
         <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 border-b border-gray-100 pb-12">
               <StatNumber number="1M+" label="Descargas" />
               <StatNumber number="4.9" label="Estrellas" />
               <StatNumber number="24/7" label="Soporte" />
               <StatNumber number="0€" label="Plan Gratuito" />
            </div>

            <div className="bg-[#000000] rounded-[40px] p-8 md:p-16 text-center text-white relative overflow-hidden">
               <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
               <div className="relative z-10">
                 <h2 className="text-3xl md:text-5xl font-bold mb-6">Empieza tu camino a la calma.</h2>
                 <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
                   Únete a miles de personas que están transformando su ansiedad en autoconocimiento.
                 </p>
                 <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <button className="px-8 py-4 bg-[#47748A] hover:bg-[#3b6174] text-white rounded-full font-bold transition-colors flex items-center justify-center gap-2">
                       <Download size={20} /> Descargar en App Store
                    </button>
                    <button className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold transition-colors flex items-center justify-center gap-2 backdrop-blur-md">
                       <Download size={20} /> Get it on Google Play
                    </button>
                 </div>
                 <p className="mt-6 text-xs text-gray-500">Plan básico gratuito para siempre. Sin tarjeta de crédito.</p>
               </div>
            </div>
         </div>
      </section>

      {/* 7. FOOTER */}
      <footer className="bg-white pt-12 pb-8 border-t border-gray-100">
         <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-1">
               <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 bg-[#47748A] rounded-lg flex items-center justify-center text-white font-bold text-xs">T</div>
                  <span className="text-lg font-bold tracking-tight text-black">trankii.</span>
               </div>
               <p className="text-gray-400 text-sm">
                 Diseñando tecnología para el bienestar humano desde 2025.
               </p>
            </div>
            
            <div>
               <h4 className="font-bold text-black mb-4">Producto</h4>
               <ul className="space-y-2 text-sm text-gray-500">
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Método</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Seguridad</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Para Empresas</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Premium</a></li>
               </ul>
            </div>

            <div>
               <h4 className="font-bold text-black mb-4">Compañía</h4>
               <ul className="space-y-2 text-sm text-gray-500">
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Misión</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Blog</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Prensa</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Contacto</a></li>
               </ul>
            </div>

            <div>
               <h4 className="font-bold text-black mb-4">Legal</h4>
               <ul className="space-y-2 text-sm text-gray-500">
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Privacidad</a></li>
                  <li><a href="/terms-of-service.html" className="hover:text-[#47748A]">Términos</a></li>
                  <li><a href="/privacy-policy.html" className="hover:text-[#47748A]">Cookies</a></li>
               </ul>
               <div className="flex gap-4 mt-6 text-gray-400">
                  <Instagram size={20} className="hover:text-[#47748A] cursor-pointer" />
                  <Twitter size={20} className="hover:text-[#47748A] cursor-pointer" />
                  <Linkedin size={20} className="hover:text-[#47748A] cursor-pointer" />
               </div>
            </div>
         </div>
         <div className="text-center text-xs text-gray-300">
            © 2025 Trankii Inc. Todos los derechos reservados.
         </div>
      </footer>
    </div>
  );
}